import { AxiosError } from 'axios'
import {
  EnumerationsDataVizSortBy,
  EnumerationsSignificanceFilter,
  type QueriesGroups,
  type TransferObjectsStrataWithValues,
} from 'client/sratool'
import { ROUTE_NAME, STORE } from '~/constants/strings'
import { HNRV2 } from '~/helpers'
import { getStoreKey } from '~/helpers/store'
import { api } from '~/services'
import { notifyFailure } from '~/utils/notifications'
import { useProjectStore } from '../projects'
import {
  type DropdownOption,
  EnumerationsSignificantResultsSummaryView,
} from '~/constants/types/projectDataVisualizations'
import {
  GET_OVERVIEW_DATA_FAILED,
  NO_ANALYSIS_SELECTED,
  NO_PROJECT_SELECTED,
} from '~/constants/projectDataVisualizations'
import type { RouteNamedMap } from 'vue-router/auto-routes'
import { GET_DATA_FAILED } from '~/constants/projectDataVisualizations'
import { useProjectDataVisualizationSelectionStore } from './projectDataVisualizationsSelection'

export const useProjectDataVisualizationsStore = defineStore(
  STORE.PROJECT_DATA_VISUALIZATIONS,
  () => {
    const ProjectStore = useProjectStore()
    const ProjectDataVisualizationSelectionStore =
      useProjectDataVisualizationSelectionStore()
    const route = useRoute()

    const hydrated = ref(false)
    const loading = ref({
      chart: false,
    })
    const dataLoadError = ref({
      message: '',
    })

    const displayDataVizErrorPageTakeover = (
      message:
        | typeof GET_DATA_FAILED
        | typeof GET_OVERVIEW_DATA_FAILED
        | typeof NO_ANALYSIS_SELECTED
        | typeof NO_PROJECT_SELECTED
    ) => {
      notifyFailure(message)
      dataLoadError.value.message = message
    }

    const showFilterRoutes: (keyof RouteNamedMap)[] = [
      ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS,
      ROUTE_NAME.COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
      ROUTE_NAME.NON_COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
      ROUTE_NAME.WORKFORCE_INSIGHTS_DATA_VISUALIZATIONS,
      ROUTE_NAME.TALENT_AVAILABILITY_METRICS_DATA_VISUALIZATIONS,
      ROUTE_NAME.RECRUITMENT_PROCESS_INSIGHTS_DATA_VISUALIZATIONS,
    ]

    const showAnalysisFilter = computed(() => {
      // Show on all routes except Project Overview Data Visualizations
      const noAnalysisFilterRoutes: (keyof RouteNamedMap)[] = [
        ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS,
      ]
      return !noAnalysisFilterRoutes.includes(route.name)
    })

    const showStrataFilter = computed(() => {
      return showFilterRoutes.includes(route.name)
    })

    const showStrataValueFilter = computed(() => {
      return showFilterRoutes.includes(route.name)
    })

    const noProjectAccess = computed(() => {
      return (
        ProjectStore.projectOptions.length === 0 ||
        !ProjectDataVisualizationSelectionStore?.projectID ||
        !ProjectStore.hasValidAnalysis
      )
    })

    const showGroupSelectFilter = computed(() => {
      const showGroupSelectRoutes: (keyof RouteNamedMap)[] = [
        ROUTE_NAME.COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
        ROUTE_NAME.NON_COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS,
        ROUTE_NAME.WORKFORCE_INSIGHTS_DATA_VISUALIZATIONS,
        ROUTE_NAME.TALENT_AVAILABILITY_METRICS_DATA_VISUALIZATIONS,
      ]
      return showGroupSelectRoutes.includes(route.name)
    })

    const significantResultsSummarySortByOptions = computed<
      DropdownOption<EnumerationsDataVizSortBy>[]
    >(() => {
      return [
        {
          value: EnumerationsDataVizSortBy.HighestStatValue,
          label: 'Highest Stat Value',
        },
        ...(!ProjectStore.isProportionalAnalysis
          ? [
              {
                value: EnumerationsDataVizSortBy.HighestShortfall,
                label: 'Highest Shortfall',
              },
            ]
          : []),
        {
          value: EnumerationsDataVizSortBy.MostTransactions,
          label: 'Most Transactions',
        },
      ]
    })

    const significantResultsSummaryViewOptions = ref<
      DropdownOption<EnumerationsSignificantResultsSummaryView>[]
    >([
      {
        value: EnumerationsSignificantResultsSummaryView.Difference,
        label: 'Difference',
      },
      {
        value: EnumerationsSignificantResultsSummaryView.Averages,
        label: 'Average',
      },
    ])

    const statisticalValuesSummaryMinimumSignificanceOptions = computed<
      DropdownOption<EnumerationsSignificanceFilter>[]
    >(() => {
      return [
        {
          value: EnumerationsSignificanceFilter.NotSignificant,
          label: ProjectStore.isProportionalAnalysis
            ? 'Not Significant (-1.64 - 1.64)'
            : 'Not Significant (< 1.64)',
        },
        {
          value: EnumerationsSignificanceFilter.ApproachSignificance,
          label: ProjectStore.isProportionalAnalysis
            ? 'Approaching Significance (≤ -1.64; ≥ 1.64)'
            : 'Approaching Significance (≥ 1.64)',
        },
        {
          value: EnumerationsSignificanceFilter.Significant,
          label: ProjectStore.isProportionalAnalysis
            ? 'Significant (≤ -1.96; ≥ 1.96)'
            : 'Significant (≥ 1.96)',
        },
        {
          value: EnumerationsSignificanceFilter.MoreSignificant,
          label: ProjectStore.isProportionalAnalysis
            ? 'Significant (≤ -3.00; ≥ 3.00)'
            : 'Significant (≥ 3.00)',
        },
        {
          value: EnumerationsSignificanceFilter.HighlySignificant,
          label: ProjectStore.isProportionalAnalysis
            ? 'Significant (≤ -4; ≥ 4)'
            : 'Significant (≥ 4)',
        },
      ]
    })

    const selectedStrata = computed<
      (typeof strataOptions.value)[number] | undefined
    >(() => {
      return strataOptions.value.find(
        (strata) =>
          strata.strataFieldGUID ===
          ProjectDataVisualizationSelectionStore.strataID
      )
    })
    const strataOptions = ref<TransferObjectsStrataWithValues[]>([])

    const selectedStrataValue = computed<
      (typeof strataValueOptions.value)[number] | undefined
    >(() => {
      return strataValueOptions.value.find(
        (strataValue) =>
          strataValue.value ===
          ProjectDataVisualizationSelectionStore.strataValue
      )
    })
    const strataValueOptions = computed<{ label: string; value: string }[]>(
      () => {
        return selectedStrata.value?.values
          ? selectedStrata.value.values.map((value) => {
              return {
                label: value,
                value: value,
              }
            })
          : []
      }
    )

    const selectedGroup = computed<(typeof groupOptions.value)[number]>(() => {
      return groupOptions.value.find(
        (group) =>
          group.groupFieldGUID ===
          ProjectDataVisualizationSelectionStore.groupFieldID
      )
    })
    const groupOptions = ref<QueriesGroups[]>([])

    const resetOptions = () => {
      ProjectStore.analysesForSelectedProject = []

      strataOptions.value = []

      groupOptions.value = []
    }

    const getFilters = async (): Promise<boolean> => {
      const success =
        route.name === ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS
          ? await getOverviewFilters()
          : await getOtherFilters()

      if (!success) {
        strataOptions.value = []
        groupOptions.value = []
      }

      validateFilterSelections()

      return success
    }

    const getOverviewFilters = async (): Promise<boolean> => {
      if (!ProjectDataVisualizationSelectionStore.isValidOverview) {
        ProjectDataVisualizationSelectionStore.updateSelection({
          project: null,
          analysis: null,
          proportionalAnalysisType: null,
          strata: null,
          strataVal: null,
          groupField: null,
        })
        return false
      }

      const response = await HNRV2({
        fn: () =>
          api.workforceAnalyticsDataVisualizations.apiSraDataVisualizationsOverviewFiltersProjectIDGet(
            ProjectDataVisualizationSelectionStore.projectID!
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Problem getting filters')

        ProjectDataVisualizationSelectionStore.updateSelection({
          project: null,
          analysis: null,
          proportionalAnalysisType: null,
          strata: null,
          strataVal: null,
          groupField: null,
        })
        return false
      }

      ProjectDataVisualizationSelectionStore.updateSelection({
        project: ProjectDataVisualizationSelectionStore.projectID,
        analysis: null,
        proportionalAnalysisType: null,
        strata: ProjectDataVisualizationSelectionStore.strataID,
        strataVal: ProjectDataVisualizationSelectionStore.strataValue,
        groupField: null,
      })

      strataOptions.value = response.strataWithValues.map((option) => {
        return {
          ...option,
          strataFieldGUID: option.strataFieldGUID || option.strataDisplayName,
        }
      })

      return true
    }

    const getOtherFilters = async (): Promise<boolean> => {
      if (!ProjectDataVisualizationSelectionStore.isValid && hydrated.value) {
        return false
      }

      const response = await HNRV2({
        fn: () =>
          api.workforceAnalyticsDataVisualizations.apiSraDataVisualizationsFiltersAnalysisIDGet(
            ProjectStore.selectedAnalysis!.analysisID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Problem getting filters')
        resetOptions()
        return false
      }

      strataOptions.value = response.strataWithValues
      groupOptions.value = response.groups

      return true
    }

    const strataOptionsForDisplay = computed<typeof strataOptions.value>(() => {
      const storeOptions = strataOptions.value
      if (!storeOptions.length) {
        return []
      }

      return [
        { strataDisplayName: '', strataFieldGUID: '', values: [] },
        ...storeOptions,
      ]
    })

    const strataValueOptionsForDisplay = computed<
      typeof strataValueOptions.value
    >(() => {
      const storeOptions = strataValueOptions.value
      if (!storeOptions.length) {
        return []
      }
      return [{ label: '', value: '' }, ...storeOptions]
    })

    const groupOptionsForDisplay = computed<typeof groupOptions.value>(() => {
      const storeOptions = groupOptions.value
      if (!storeOptions.length) {
        return []
      }
      return [{ groupFieldGUID: '', groupDisplayName: '' }, ...storeOptions]
    })

    const validateFilterSelections = () => {
      const invalidSelectedGroup =
        selectedGroup.value &&
        !groupOptionsForDisplay.value.find(
          (group) =>
            group.groupFieldGUID === selectedGroup.value?.groupFieldGUID
        )
      const invalidSelectedStrata =
        selectedStrata.value &&
        !strataOptionsForDisplay.value.find(
          (strata) =>
            strata.strataFieldGUID === selectedStrata.value?.strataFieldGUID
        )
      const invalidSelectedStrataValue =
        selectedStrataValue.value &&
        !strataValueOptionsForDisplay.value.find(
          (strataValue) =>
            strataValue.value === selectedStrataValue.value?.value
        )

      const updatedGroup = invalidSelectedGroup
        ? null
        : ProjectDataVisualizationSelectionStore.groupFieldID
      const updatedStrata = invalidSelectedStrata
        ? null
        : ProjectDataVisualizationSelectionStore.strataID
      const updatedStrataValue =
        invalidSelectedStrata || invalidSelectedStrataValue
          ? null
          : ProjectDataVisualizationSelectionStore.strataValue

      ProjectDataVisualizationSelectionStore.updateSelection({
        project: ProjectDataVisualizationSelectionStore.projectID,
        analysis: ProjectDataVisualizationSelectionStore.analysisID,
        proportionalAnalysisType:
          ProjectDataVisualizationSelectionStore.proportionalAnalysisTypeID,
        strata: updatedStrata,
        strataVal: updatedStrataValue,
        groupField: updatedGroup,
      })
    }

    const selectedFilters = computed(() => {
      const strata =
        selectedStrata.value?.strataFieldGUID &&
        selectedStrataValue.value?.value
          ? selectedStrata.value
          : undefined
      const strataValue =
        strata && selectedStrataValue.value?.value
          ? selectedStrataValue.value
          : undefined

      return {
        selectedAnalysis: ProjectStore.selectedAnalysis!,
        selectedStrata: strata,
        selectedStrataValue: strataValue,
        selectedGroup: selectedGroup.value,
      }
    })

    return {
      hydrated,
      loading,
      dataLoadError,
      noProjectAccess,
      displayDataVizErrorPageTakeover,

      showAnalysisFilter,
      showStrataFilter,
      showStrataValueFilter,
      showGroupSelectFilter,

      selectedStrata,
      strataOptions,
      strataOptionsForDisplay,

      selectedStrataValue,
      strataValueOptions,
      strataValueOptionsForDisplay,

      selectedGroup,
      groupOptions,
      groupOptionsForDisplay,

      statisticalValuesSummaryMinimumSignificanceOptions,
      significantResultsSummarySortByOptions,
      significantResultsSummaryViewOptions,

      selectedFilters,

      getFilters,
    }
  },
  {
    persist: {
      key: getStoreKey(STORE.PROJECT_DATA_VISUALIZATIONS),
      pick: [
        'selectedStrata',
        'strataOptions',

        'selectedStrataValue',
        'strataValueOptions',

        'selectedGroup',
        'groupOptions',
      ],
    },
  }
)
